/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { SalesFolderRestService, SalesFolderStatus } from '@gh/builder-shared-data';
import { ContextRoute, getContextRouteInitData } from '@gh/common-internal-shared';
import {
  AbstractSnapshotFormComponent,
  DateTimeTypes,
  DictionaryItem,
  EnumDictionary,
  FormGroupSchemaConfig,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  toAppFormGroup,
  updateSchemaWithChildren,
} from '@gh/common-shared';
import { assign, isString } from 'lodash';
import {
  LeadSalesFolder,
  LeadSalesFolderMeeting,
  LeadSfJobDetails,
} from '../../../resources/lead.model';
import { LeadDetailsCommandService } from '../lead-details-command.service';
import { ID } from '@gh/common-shared-data';
import { FormMode, isEditFormMode } from '@gh/core-ui';
import { LeadDetailsBusinessService } from '../../../resources/lead-details-business.service';
import { Subscription } from 'rxjs';

const EDITABLE_SALES_FOLDER_STATUSES: SalesFolderStatus[] = [
  SalesFolderStatus.Active,
  SalesFolderStatus.WillNotComplete,
];

@Component({
  standalone: false,
  selector: 'gh-lead-details-sf-form',
  templateUrl: 'lead-details-sf-form.component.html',
  styleUrls: ['lead-details-sf-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeadDetailsSfFormComponent extends AbstractSnapshotFormComponent<LeadSalesFolder> implements OnInit {
  @Input() jobDetails?: LeadSfJobDetails;
  @Input() sfId?: ID;
  @Input() formMode: FormMode;
  @Output() activityChange = new EventEmitter();

  filteredStatuses: EnumDictionary<SalesFolderStatus>;
  notProceedingReasons: any[];
  priorities: DictionaryItem<any>[];
  stages: DictionaryItem<any>[];
  followUp?: Date;
  earliestActivityId?: ID;
  private onActivityChanges: Subscription;
  private statuses: EnumDictionary<SalesFolderStatus>;

  get isFollowUpDateVisible(): boolean {
    return this.formMode !== FormMode.CreateMode && (!!this.sfId && !this.sfId.includes('sf:')) ;
  }

  get editMode(): boolean {
    return isEditFormMode(this.formMode);
  }

  get isEditActivityActive(): boolean {
    return  !!this.earliestActivityId && !!this.followUp && this.formMode === FormMode.EditMode;
  }

  constructor(private route: ContextRoute,
              private leadService: LeadDetailsBusinessService,
              private commands: LeadDetailsCommandService,
              private sfService: SalesFolderRestService,
              private changeDetection: ChangeDetectorRef,
              private formStateBuilder: FormStateBuilder) {
    super();
  }

  get isDppJobExist(): boolean {
    const { jobDetails } = this;
    return jobDetails ? jobDetails.isDpp : false;
  }

  get isRegularJobExist(): boolean {
    const { jobDetails } = this;
    return jobDetails ? !jobDetails.isDpp : false;
  }

  ngOnInit(): void {
    const init = getContextRouteInitData(this.route);
    this.onActivityChanges = this.leadService.activityWasChanges.subscribe((status) => {
      if (this.sfId && !this.sfId.startsWith('sf:') && status !== null) {
        this.sfService.get(this.sfId).subscribe((sf) => {
          this.followUp = sf.followUp;
          this.earliestActivityId = sf.earliestActivityId;
          this.changeDetection.markForCheck();
        });
      }
    });
    super.ngOnInit();

    assign(this, init);
    this.buildStatusDictionary(getFormStateSnapshotValue(this.state).status);
    this.setNotProceedingReasons(getFormStateSnapshotValue(this.state).notProceedingReason);
  }

  onStatusChange(): void {
    const isStatusWillNotComplete = this.form.value.status === SalesFolderStatus.WillNotComplete;
    this.form.get('notProceedingReason').visible = isStatusWillNotComplete;
    if (isStatusWillNotComplete) {
      this.form.get('notProceedingReason').enable();
      return;
    }
    this.form.get('notProceedingReason').setValue('');
    this.form.get('notProceedingReason').disable();
    this.form.get('notProceedingReason').reset();
  }

  onEditActivity(): void {
    if (!!this.sfId && isString(this.sfId)) {
      const meeting = {
        allDay: false,
        id: this.earliestActivityId,
        activityType: '',
        startDate: new Date(),
        endDate: new Date(),
        guests: [],
        location: '',
        description: '',
        notes: '',
        isDone: false,
        assignee: '',
        active: true,
      } as LeadSalesFolderMeeting;
      this.commands.meetingOpen(this.sfId, meeting, () => {
        this.leadService.activityWasChanges.next(meeting.id);
        this.activityChange.emit();
      });
    }
  }

  protected buildForm(snapshot: FormStateSnapshot<LeadSalesFolder>): FormState<LeadSalesFolder> {
    const salesFolder = getFormStateSnapshotValue(snapshot);
    this.followUp = salesFolder.followUp;
    this.earliestActivityId = salesFolder.earliestActivityId;
    this.changeDetection.markForCheck();
    return this.formStateBuilder.buildPlain({
      snapshot,
      ...this.getSalesFolderSchema(salesFolder),
    });
  }

  protected afterRestored(current: FormStateSnapshot<LeadSalesFolder>): void {
    this.followUp = current.value.followUp;
    this.earliestActivityId = current.value.earliestActivityId;
    this.changeDetection.markForCheck();
    updateSchemaWithChildren(
      toAppFormGroup(this.form.group),
      this.getSalesFolderSchema(getFormStateSnapshotValue(current)));
    this.buildStatusDictionary(getFormStateSnapshotValue(current).status);
  }

  private setNotProceedingReasons(value?: string | number): void {
    if (value) {
      this.form.getControl('notProceedingReason').setValue(`${value}`);
    }
  }

  private buildStatusDictionary(status: SalesFolderStatus): void {
    this.filteredStatuses = status === SalesFolderStatus.Completed
      ? this.statuses
      : this.statuses.filter((item) => EDITABLE_SALES_FOLDER_STATUSES.includes(item.id));
  }

  private getSalesFolderSchema(sf: LeadSalesFolder): FormGroupSchemaConfig {
    return {
      enabled: this.enabled,
      children: {
        name: { required: true },
        priority: { required: true },
        created: { enabled: false, type: DateTimeTypes.ShortDate },
        followUp: { type: DateTimeTypes.ShortDate, enabled: false },
        status: { required: true, enabled: sf.status !== SalesFolderStatus.Completed },
        notProceedingReason: {
          required: true,
          enabled: sf.status === SalesFolderStatus.WillNotComplete,
          visible: sf.status === SalesFolderStatus.WillNotComplete },
        stage: { required: true },
      },
    };
  }
}
